/* eslint-disable no-param-reassign */
import Dynamic from 'next/dynamic';
import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useSelector, useDispatch } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { UpdateCourse } from 'store/actions/schoolDetailActions';

import { getUpcomingStartDatesText, getBreakDates } from 'utils/courseUtils';
import { sortBySelected } from 'utils/arrayUtils';
import { shouldUnlockPrice } from 'utils/lead';
import { formatDate } from 'utils/date';

export const PriceComponent = Dynamic(
  () => import('components/PriceComponent'),
  { ssr: false }
);

export const SelectedButton = Dynamic(
  () => import('components/Buttons/SelectedButtonComponent'),
  { ssr: false }
);

export const DescriptionButton = Dynamic(
  () => import('components/Buttons/DescriptionButtonComponent'),
  { ssr: false }
);

export const UnlockEstimateComponent = Dynamic(
  () => import('components/UnlockEstimateComponent'),
  { ssr: false }
);

function CourseListItemComponent({ dateHandler }) {
  const dispatch = useDispatch();
  const { courses } = useSelector((store) => store.schoolDetailReducer);
  const { user } = useUser();
  const { lead } = useSelector((store) => store.studentReducer);
  const { dateParams, setDateParams } = dateHandler;
  const [seeMore, setSeeMore] = useState(3);
  const handleSeeMore = () => setSeeMore(courses?.length);
  const handleMeetingScheduled =
    !shouldUnlockPrice(lead, user) && lead?.hasMeetingScheduled;

  const setProfessionCourseWeek = () => {
    const course = courses.find((c) => c.selected && c.program.id === 2);
    const range = course && course.priceRanges[0].rangeFrom;

    if (range) {
      dateParams.courseWeeks = range;
      setDateParams(dateParams);
    }
  };

  const toggleCourse = (id) => {
    courses.filter((course) => {
      course.selected = course.id === id;
      course.aboutToggle = course.id === id;
    });

    UpdateCourse(dispatch, courses);
  };

  const toggleCourseDescription = (id) => {
    courses.filter((course) => {
      if (course.aboutToggle === undefined) course.aboutToggle = false;
      if (course.id === id) course.aboutToggle = !course.aboutToggle;
    });

    UpdateCourse(dispatch, courses);
  };

  useEffect(() => {
    if (!courses) courses[0].aboutToggle = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses[0]]);

  if (courses.length > 0) {
    return (
      <>
        <List>
          {courses
            .slice(0, seeMore)
            .sort(sortBySelected)
            .map((course) => {
              const price = course.priceRanges[0].exchanged;
              const priceWithDiscount = course.priceRanges[0].totalExchanged;

              return (
                <ListItem disableGutters key={course.id}>
                  <Box
                    bgcolor="background.paper"
                    border={1}
                    borderRadius="4px"
                    borderColor={course.selected ? 'text.primary' : '#C3C2C2E8'}
                    boxShadow={course.selected ? 5 : 0}
                    flexGrow={1}
                    textAlign="left"
                  >
                    <Box p={2}>
                      <Typography
                        component="h4"
                        sx={{ fontWeight: 900, letterSpacing: 0 }}
                        variant="h5"
                      >
                        {course.type} {course.title && <>- {course.title}</>}
                      </Typography>

                      <Typography variant="body2">
                        {course.classesQuantity} por semana - {course.period}
                      </Typography>

                      <Collapse
                        key={`course-desc-${course.id}`}
                        in={!!course?.aboutToggle}
                      >
                        <Box my={1}>
                          <Divider />
                        </Box>

                        <List>
                          <ListItem>
                            <Typography variant="body2">
                              &bull; O curso de {course.type} {course.title} é
                              ministrado em {course.language.description} de{' '}
                              {course.schedule} {course.period} com{' '}
                              {course.classesQuantity} por semana.
                            </Typography>
                          </ListItem>

                          {course.typeDescription && (
                            <ListItem>
                              <Typography variant="body2">
                                &bull; {course.typeDescription}
                              </Typography>
                            </ListItem>
                          )}

                          {course.about && (
                            <ListItem>
                              <Typography variant="body2">
                                &bull; {course.about}
                              </Typography>
                            </ListItem>
                          )}

                          {course.bookMaterial.currency &&
                            course.bookChargedBefore === false && (
                              <ListItem>
                                <Typography variant="body2">
                                  &bull; O material didático tem um custo de{' '}
                                  {course.bookMaterial.currency.symbol}{' '}
                                  {course.bookMaterial.price} e deve ser pago
                                  diretamente na escola.
                                </Typography>
                              </ListItem>
                            )}

                          {course.rules && (
                            <ListItem>
                              <Typography variant="body2">
                                &bull; {course.rules}
                              </Typography>
                            </ListItem>
                          )}

                          {course.breaks && course.breaks.length > 0 && (
                            <ListItem>
                              <Typography variant="body2">
                                &bull; {getBreakDates(course.breaks)}
                              </Typography>
                            </ListItem>
                          )}

                          {course.startDates && (
                            <ListItem
                              sx={{
                                border: '1px solid #367CE7',
                                borderRadius: '4px',
                                marginTop: '8px',
                              }}
                            >
                              <Grid container>
                                <Grid item xs={12} sm={3}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      alignItems: 'center',
                                      color: '#367CE7',
                                      display: 'flex',
                                      fontWeight: 600,
                                      height: '100%',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    <DateRangeIcon
                                      sx={{ marginRight: '8px' }}
                                    />
                                    Datas de início:
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sm={9}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      alignItems: 'center',
                                      display: 'flex',
                                      height: '100%',
                                    }}
                                  >
                                    {getUpcomingStartDatesText(
                                      course.startDates
                                    )}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </ListItem>
                          )}
                        </List>

                        {course.priceRanges.map((pr) =>
                          pr.supplements.map((supplement) => (
                            <Box py={2} key={supplement.id}>
                              <MuiAlert
                                variant="outlined"
                                severity="warning"
                                icon={<TrendingUpIcon fontSize="inherit" />}
                                sx={{
                                  filter: shouldUnlockPrice(lead, user)
                                    ? 'none'
                                    : 'blur(6px)',
                                  userSelect: shouldUnlockPrice(lead, user)
                                    ? 'initial'
                                    : 'none',
                                }}
                              >
                                {supplement?.description && (
                                  <>
                                    <Box fontWeight={600} component="span">
                                      {ReactHtmlParser(supplement.description)}:
                                    </Box>
                                    &nbsp;
                                  </>
                                )}
                                Entre{' '}
                                {formatDate(
                                  supplement.startDate,
                                  'day-and-month'
                                )}{' '}
                                e{' '}
                                {formatDate(
                                  supplement.endDate,
                                  'day-and-month'
                                )}
                                , há um acréscimo de{' '}
                                <PriceComponent
                                  value={supplement.price * pr.exchangeRateUsed}
                                />{' '}
                                por semana.
                              </MuiAlert>
                            </Box>
                          ))
                        )}
                      </Collapse>
                    </Box>

                    <Divider />

                    <Box
                      borderColor={
                        course.selected ? 'text.primary' : 'text.secondary'
                      }
                      p={2}
                      bgcolor="background.secondary.main"
                      borderRadius="0 0 4px 4px"
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={7}
                          sx={{
                            filter: shouldUnlockPrice(lead, user)
                              ? 'none'
                              : 'blur(6px)',
                            userSelect: shouldUnlockPrice(lead, user)
                              ? 'initial'
                              : 'none',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h5" fontWeight="bold">
                              <PriceComponent value={priceWithDiscount} />
                            </Typography>

                            {price > priceWithDiscount && (
                              <Typography
                                variant="h5"
                                sx={{
                                  color: '#8a8a8a',
                                  fontWeight: 'bold',
                                  marginLeft: '16px',
                                  textDecoration: 'line-through',
                                }}
                              >
                                <PriceComponent value={price} />
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        <Grid
                          item
                          xs={handleMeetingScheduled ? 12 : 5}
                          sm={3}
                          md={2}
                          sx={{
                            textAlign: handleMeetingScheduled
                              ? 'center'
                              : 'none',
                          }}
                        >
                          <DescriptionButton
                            key={`course-about-${course.id}`}
                            onClick={() => toggleCourseDescription(course.id)}
                            size="large"
                          >
                            <Box display="flex" alignItems="center" pl={0.4}>
                              {course.aboutToggle ? (
                                <RemoveIcon fontSize="small" />
                              ) : (
                                <AddIcon fontSize="small" />
                              )}
                              &nbsp;Detalhes
                            </Box>
                          </DescriptionButton>
                        </Grid>

                        {shouldUnlockPrice(lead, user) ? (
                          <Grid item xs={7} sm={3}>
                            {course.selected ? (
                              <SelectedButton
                                disableElevation
                                disableFocusRipple
                                disableRipple
                                fullWidth
                                size="large"
                                variant="contained"
                              >
                                Selecionado
                              </SelectedButton>
                            ) : (
                              <Button
                                color="secondary"
                                fullWidth
                                onClick={() => {
                                  toggleCourse(course.id);
                                  setProfessionCourseWeek();
                                }}
                                size="large"
                                variant="contained"
                              >
                                Selecionar
                              </Button>
                            )}
                          </Grid>
                        ) : (
                          <Grid
                            item
                            xs={handleMeetingScheduled ? 12 : 7}
                            md={3}
                            sm={3}
                          >
                            <Box sx={{ '& > button': { minHeight: '42px' } }}>
                              <UnlockEstimateComponent />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
        </List>

        {seeMore !== courses?.length && courses?.length > 3 && (
          <Box my={1} textAlign="center">
            <Button onClick={handleSeeMore} variant="text">
              Ver mais
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <Typography variant="h6">
      <Box textAlign="center">
        Não há cursos disponíveis para essa quantidade de semanas.
      </Box>
    </Typography>
  );
}

CourseListItemComponent.propTypes = {
  dateHandler: PropTypes.instanceOf(Object).isRequired,
};

export default memo(CourseListItemComponent);
